/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import type { MenuItemType } from '@opengeoweb/shared';
import { ToggleMenu } from '@opengeoweb/shared';
import { Options } from '@opengeoweb/theme';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import type { Warning } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import type { WarningListItemActions } from '../PublicWarningList/WarningListItemMenu';
import WarningListItemMenu from '../PublicWarningList/WarningListItemMenu';
import { WarningLevel } from '../PublicWarningsForm/LevelField';

interface WarningBlockMenuProps {
  effectiveStatus: PublicWarningStatus;
  hasWarningContent: boolean;
  isAviation: boolean;
  level: WarningLevel | null;
  onMenuInteraction?: () => void;
  onMenuLeave?: () => void;
  warning: Warning;
  warningListItemActions?: WarningListItemActions;
}

export const WarningBlockMenu: React.FC<WarningBlockMenuProps> = ({
  effectiveStatus,
  hasWarningContent,
  isAviation,
  level,
  onMenuInteraction,
  onMenuLeave,
  warning,
  warningListItemActions,
}) => {
  const { t } = useWarningsTranslation();
  const theme = useTheme();

  if (!warningListItemActions) {
    return null;
  }

  const tertiaryButton = theme.palette.geowebColors.buttons.tertiary;
  const primaryButton = theme.palette.geowebColors.buttons.primary;
  const defaultIconColor = theme.palette.geowebColors.typographyAndIcons.icon;
  const primaryIconColor =
    theme.palette.geowebColors.typographyAndIcons.buttonIcon;
  const menuButtonSize = theme.spacing(2.5);
  const menuButtonBorderWidth = theme.spacing(0.125);
  const menuIconColor =
    effectiveStatus === PublicWarningStatus.PUBLISHED &&
    (isAviation ||
      level === WarningLevel.extreme ||
      level === WarningLevel.severe)
      ? primaryIconColor
      : defaultIconColor;
  const hoverMenuIconColor =
    theme.palette.mode === 'dark' ? primaryIconColor : defaultIconColor;

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        onMenuInteraction?.();
      }}
      onFocus={onMenuInteraction}
      onKeyDown={(event) => {
        event.stopPropagation();
        onMenuInteraction?.();
      }}
      onMouseDown={onMenuInteraction}
      onMouseEnter={onMenuInteraction}
      onMouseLeave={onMenuLeave}
      sx={{
        display: 'flex',
        flex: `0 0 ${menuButtonSize}`,
        margin: hasWarningContent ? undefined : '0 auto',
      }}
    >
      <ToggleMenu
        buttonIcon={<Options />}
        buttonSx={{
          backgroundColor: tertiaryButton.default.fill,
          border: `${menuButtonBorderWidth} solid ${tertiaryButton.default.fill}`,
          color: menuIconColor,
          height: menuButtonSize,
          minHeight: menuButtonSize,
          minWidth: menuButtonSize,
          padding: 0,
          width: menuButtonSize,
          '&.flat': {
            backgroundColor: tertiaryButton.default.fill,
            borderColor: tertiaryButton.default.fill,
            color: menuIconColor,
          },
          '&.flat:hover, &.flat.Mui-focusVisible': {
            backgroundColor: tertiaryButton.mouseOver.fill,
            borderColor: tertiaryButton.default.borderColor,
            color: hoverMenuIconColor,
          },
          '&.flat:active, &.flat.Mui-selected, &.flat.Mui-selected:hover': {
            backgroundColor: primaryButton.default.fill,
            borderColor: tertiaryButton.default.borderColor,
            color: primaryIconColor,
          },
          '&.flat[class*="sizeSmall"]': {
            height: menuButtonSize,
            width: menuButtonSize,
          },
          '&.flat.Mui-focusVisible': {
            outline: tertiaryButton.focus.outline,
            outlineOffset: 0,
          },
        }}
        buttonTestId={`warning-block-menu-${warning.id ?? 'unknown'}`}
        menuItems={
          WarningListItemMenu({
            warning,
            t,
            ...warningListItemActions,
          }) as unknown as MenuItemType[]
        }
        menuPosition="bottom"
        menuTitle={t('warning-list-button-options')}
        tooltipTitle={t('warning-list-button-options')}
        variant="flat"
      />
    </Box>
  );
};
